/**
 * Dormant Users Card Component
 * Displays AI-powered dormant user insights in the Analytics Dashboard
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Button,
  TablePagination,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Psychology as AIIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { PageHeader } from '../../shared';
import profile360Service from '../services/profile360Service';
import { useUserMetadata } from '../../../context/UserMetadataContext';

/**
 * Expandable row component for detailed user insights
 */
function DormantUserRow({ user }) {
  const [open, setOpen] = useState(false);

  const getRiskColor = (score) => {
    if (score >= 9) return 'error';
    if (score >= 7) return 'warning';
    if (score >= 5) return 'info';
    return 'success';
  };

  const getClassificationColor = (classification) => {
    switch (classification?.toUpperCase()) {
      case 'DORMANT':
        return 'error';
      case 'AT_RISK':
        return 'warning';
      case 'ACTIVE':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <TableCell>
          <IconButton size="small">
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{user.user_email}</TableCell>
        <TableCell>
          <Chip
            label={user.classification || 'UNKNOWN'}
            color={getClassificationColor(user.classification)}
            size="small"
          />
        </TableCell>
        <TableCell>
          <Chip
            label={user.risk_score}
            color={getRiskColor(user.risk_score)}
            size="small"
          />
        </TableCell>
        <TableCell>{user.days_inactive || 'N/A'}</TableCell>
        <TableCell>{user.job_title || 'N/A'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Grid2 container spacing={2}>
                {/* AI Reasoning */}
                <Grid2 xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AIIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="subtitle2" fontWeight="bold">
                        AI Analysis
                      </Typography>
                    </Box>
                    <Typography variant="body2" paragraph>
                      <strong>Root Cause:</strong> {user.root_cause || 'Not specified'}
                    </Typography>
                    <Typography variant="body2">
                      {user.reasoning || 'No reasoning provided'}
                    </Typography>
                  </Paper>
                </Grid2>

                {/* Risk Factors */}
                {user.risk_factors && Array.isArray(user.risk_factors) && user.risk_factors.length > 0 && (
                  <Grid2 xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'rgba(244,67,54,0.08)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <WarningIcon sx={{ mr: 1, color: 'error.main' }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Risk Factors
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 2, m: 0 }}>
                        {user.risk_factors.map((factor, idx) => {
                          // Handle both string and object formats
                          const factorText = typeof factor === 'string' 
                            ? factor 
                            : (typeof factor === 'object' && factor !== null)
                              ? JSON.stringify(factor)
                              : String(factor);
                          return (
                            <li key={idx}>
                              <Typography variant="body2">{factorText}</Typography>
                            </li>
                          );
                        })}
                      </Box>
                    </Paper>
                  </Grid2>
                )}

                {/* Recommendations */}
                {user.recommendations && Array.isArray(user.recommendations) && user.recommendations.length > 0 && (
                  <Grid2 xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'rgba(76,175,80,0.08)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Recommendations
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 2, m: 0 }}>
                        {user.recommendations.map((rec, idx) => {
                          // Handle both string and object formats
                          if (typeof rec === 'string') {
                            return (
                              <li key={idx}>
                                <Typography variant="body2">{rec}</Typography>
                              </li>
                            );
                          } else if (typeof rec === 'object' && rec !== null) {
                            // Handle object format with action, priority, rationale
                            return (
                              <li key={idx}>
                                <Typography variant="body2" fontWeight="medium">
                                  {rec.action || 'No action specified'}
                                </Typography>
                                {rec.priority && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Priority: {rec.priority}
                                  </Typography>
                                )}
                                {rec.rationale && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {rec.rationale}
                                  </Typography>
                                )}
                              </li>
                            );
                          }
                          return null;
                        })}
                      </Box>
                    </Paper>
                  </Grid2>
                )}

                {/* Mitigating Factors */}
                {user.mitigating_factors && Array.isArray(user.mitigating_factors) && user.mitigating_factors.length > 0 && (
                  <Grid2 xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'rgba(33,150,243,0.08)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircleIcon sx={{ mr: 1, color: 'info.main' }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Mitigating Factors
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 2, m: 0 }}>
                        {user.mitigating_factors.map((factor, idx) => {
                          const factorText = typeof factor === 'string'
                            ? factor
                            : (typeof factor === 'object' && factor !== null)
                              ? JSON.stringify(factor)
                              : String(factor);
                          return (
                            <li key={idx}>
                              <Typography variant="body2">{factorText}</Typography>
                            </li>
                          );
                        })}
                      </Box>
                    </Paper>
                  </Grid2>
                )}

                {/* Activity Summary */}
                {user.activity_summary && (
                  <Grid2 xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'rgba(33,150,243,0.08)' }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Activity Summary
                      </Typography>
                      <Grid2 container spacing={1}>
                        {Object.entries(user.activity_summary).map(([key, value]) => (
                          <Grid2 xs={6} md={3} key={key}>
                            <Typography variant="caption" color="text.secondary">
                              {key.replace(/_/g, ' ').toUpperCase()}
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </Typography>
                          </Grid2>
                        ))}
                      </Grid2>
                    </Paper>
                  </Grid2>
                )}

                {/* Metadata */}
                <Grid2 xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Analysis Date: {new Date(user.analysis_date).toLocaleString()} |
                    Model: {user.model_used || 'Unknown'} |
                    Confidence: {user.confidence_score ? `${(user.confidence_score * 100).toFixed(1)}%` : 'N/A'}
                  </Typography>
                </Grid2>
              </Grid2>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/**
 * Main Dormant Users Card Component
 */
function DormantUsersCard({ loading: parentLoading }) {
  const { getAccessTokenSilently } = useAuth0();
  const { userMetadata } = useUserMetadata();
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dormantUsers, setDormantUsers] = useState([]);
  const [riskDistribution, setRiskDistribution] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters and pagination
  const [classification, setClassification] = useState('all');
  const [minRiskScore, setMinRiskScore] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /**
   * Load dormant users with filters
   */
  const loadDormantUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Initialize service
      const token = await getAccessTokenSilently();
      const tenantName = userMetadata?.company;
      if (!tenantName) {
        throw new Error('Tenant information not available');
      }
      profile360Service.initialize(token, tenantName);

      const filters = {
        page: page + 1, // API uses 1-based pagination
        pageSize: rowsPerPage,
      };

      if (classification !== 'all') {
        filters.classification = classification.toUpperCase();
      }

      if (minRiskScore && !isNaN(minRiskScore)) {
        filters.minRiskScore = parseInt(minRiskScore);
      }

      const response = await profile360Service.getAIDormantInsights(filters);

      if (response.success) {
        // Backend returns { success: true, data: [...], total: 54 }
        // ApiService wraps it: { success: true, data: { success: true, data: [...], total: 54 } }
        // So we need response.data.data and response.data.total
        setDormantUsers(response.data?.data || []);
        setTotalCount(response.data?.total || 0);
      } else {
        throw new Error(response.error || 'Failed to load dormant users');
      }
    } catch (err) {
      console.error('Error loading dormant users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, userMetadata, classification, minRiskScore, page, rowsPerPage]);

  /**
   * Load risk distribution for charts
   */
  const loadRiskDistribution = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const tenantName = userMetadata?.company;
      if (!tenantName) return;
      profile360Service.initialize(token, tenantName);

      const response = await profile360Service.getRiskDistribution();
      if (response.success) {
        // Backend returns { success: true, data: {...} }
        // ApiService wraps it: { success: true, data: { success: true, data: {...} } }
        // So we need response.data.data
        setRiskDistribution(response.data?.data || null);
      }
    } catch (err) {
      console.error('Error loading risk distribution:', err);
    }
  }, [getAccessTokenSilently, userMetadata]);

  // Load data on mount and filter changes
  useEffect(() => {
    loadDormantUsers();
    loadRiskDistribution();
  }, [loadDormantUsers, loadRiskDistribution]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    loadDormantUsers();
    loadRiskDistribution();
  };

  // Chart colors
  const COLORS = ['#f44336', '#ff9800', '#2196f3', '#4caf50', '#9e9e9e'];

  return (
    <Box>
      <PageHeader
        title="Dormant Users"
        description="AI-powered detection of inactive accounts with active access permissions"
        pillarId="risk"
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters and Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select
          label="Classification"
          value={classification}
          onChange={(e) => setClassification(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="dormant">Dormant</MenuItem>
          <MenuItem value="at_risk">At Risk</MenuItem>
          <MenuItem value="active">Active</MenuItem>
        </TextField>

        <TextField
          label="Min Risk Score"
          type="number"
          value={minRiskScore}
          onChange={(e) => setMinRiskScore(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
          inputProps={{ min: 0, max: 10 }}
        />

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
          size="small"
        >
          Refresh
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        <Typography variant="body2" color="text.secondary">
          Total: {totalCount} users
        </Typography>
      </Box>

      {/* Risk Distribution Charts */}
      {riskDistribution && (
        <Grid2 container spacing={2} sx={{ mb: 2 }}>
          <Grid2 xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Classification Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={riskDistribution.classification_distribution || []}
                      dataKey="count"
                      nameKey="classification"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={(entry) => `${entry.classification}: ${entry.count}`}
                    >
                      {(riskDistribution.classification_distribution || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Risk Score Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={riskDistribution.risk_score_distribution || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="risk_range" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#2196f3" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>
      )}

      {/* Dormant Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="50px" />
              <TableCell>Email</TableCell>
              <TableCell>Classification</TableCell>
              <TableCell>Risk Score</TableCell>
              <TableCell>Days Inactive</TableCell>
              <TableCell>Job Title</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : dormantUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No dormant users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              dormantUsers.map((user) => (
                <DormantUserRow key={user.user_email} user={user} />
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>
    </Box>
  );
}

export default DormantUsersCard;
